import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useState } from "react";
import {
  fetchAdminUnitDetail,
  fetchChildAdminUnits,
} from "services/advocateComplaintService";
import {
  addInvestigationFeedback,
  editComplaint,
  editInvestigation,
} from "services/prosecutorDecipilineService";
import { AdminUnit, Complaints, Investigation } from "types";
import { LoadingIcon } from "utils/constant";
import {
  COMPLIANT_STATUS,
  FEEDBACK_TYPE,
  HIERARCHY_LEVELS,
  INVESTIGATION_STATUS,
  PROSECUTOR_DECISION_TYPE2,
} from "utils/constantValues";
import "../styles/AddEvidenceModal.css";

interface FeedbackModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  investigationData: Investigation;
  onRefresh: any;
}

export default function AddFeedbackModal({
  isOpen,
  setIsOpen,
  complaintData,
  investigationData,
  onRefresh,
}: FeedbackModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  // const [success, setSuccess] = useState(false);

  const [adminUnitData, setAdminUnitData] = useState<AdminUnit>();
  const [childAdminUnit, setChildAdminUnit] = useState<any>([]);
  const [selectedAdminUnit, setSelectedAdminUnit] = useState<any>();

  const [formState, setFormState] = useState<Investigation>({
    feedback_status: FEEDBACK_TYPE?.APPROVED,
    feedback: "",
    complaint: null,
    status: INVESTIGATION_STATUS?.UNDER_INVESTIGATION,
  });

  useEffect(() => {
    const loadAdminUnitDetail = async () => {
      try {
        const adminUnitDetail = await fetchAdminUnitDetail(
          user?.admin_unit,
          user?.access,
        );
        setAdminUnitData(adminUnitDetail);
      } catch (err) {
        console.error("Failed to load admin unit detail:", err);
      }
    };
    loadAdminUnitDetail();
  }, [user]);

  useEffect(() => {
    const loadChildAdminUnit = async () => {
      try {
        const adminUnitDetail = await fetchChildAdminUnits(
          adminUnitData?.uu_id || "",
          user?.access,
        );
        setChildAdminUnit(adminUnitDetail?.units);
      } catch (err) {
        console.error("Failed to load child admin units:", err);
      } finally {
        setLoading(false);
      }
    };
    adminUnitData?.uu_id && loadChildAdminUnit();
  }, [adminUnitData, user?.access]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    // setError(null);
    // setSuccess(false);

    if (!investigationData) return;

    const data = {
      ...formState,

      assigned_prosecutor: complaintData?.user_id_accused_prosecutor_name || "",
      user_id_assigned_prosecutor:
        complaintData?.user_id_accused_prosecutor_name || "",
      accused_prosecutor_name: complaintData?.accused_prosecutor_name || "",
      user_id_accused_prosecutor_name:
        complaintData?.user_id_accused_prosecutor_name || "",
      accused_prosecutor_org_id: complaintData?.accused_prosecutor_org_id || "",
      accused_prosecutor_admin_unit_id:
        complaintData?.accused_prosecutor_admin_unit_id || "",
      admin_unit_id: complaintData?.admin_unit_id || "",
      org_id: complaintData?.org_id || "",
      complaint: complaintData?.uu_id || null,
    };
    const investigation_id = investigationData?.uu_id || "";
    const isFormData = false;

    try {
      const resData = await editInvestigation(
        data,
        investigation_id,
        isFormData,
      );

      if (resData) {
        await addInvestigationFeedback({
          investigation: investigation_id,
          comment: formState.feedback || "",
        });

        (formState.feedback_status === FEEDBACK_TYPE.REJECT_TO_CLOSE ||
          (investigationData?.prosecutor_decision_type ===
            PROSECUTOR_DECISION_TYPE2.CLOSE_THE_CASE &&
            formState.feedback_status === FEEDBACK_TYPE.APPROVED)) &&
          (await editComplaint({
            ...complaintData,
            status: COMPLIANT_STATUS.CLOSED,
          }));

        if (
          investigationData?.prosecutor_decision_type ===
            "out of jurisdiction forward to zone" ||
          investigationData?.prosecutor_decision_type ===
            "Forwarded to zone for further investigation"
        ) {
          await editComplaint({
            ...complaintData,
            status:
              investigationData?.prosecutor_decision_type ===
              "out of jurisdiction forward to zone"
                ? COMPLIANT_STATUS.FORWARDED
                : COMPLIANT_STATUS.MORE_INFORMATION_REQUESTED,
            admin_unit_id: selectedAdminUnit,
            // assigned_prosecutor:
            //   complaintData?.status ===
            //   "Forwarded to zone for further investigation"
            //     ? ""
            //     : undefined,
            // user_id_assigned_prosecutor:
            //   complaintData?.status ===
            //   "Forwarded to zone for further investigation"
            //     ? ""
            //     : undefined,
            // decision_type: "",
          });
        }
      }

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      // setSuccess(true);
      setFormState({
        feedback_status: FEEDBACK_TYPE?.APPROVED,
        feedback: "",
        complaint: null,
        status: INVESTIGATION_STATUS?.UNDER_INVESTIGATION,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
      // setError(
      //   err?.response?.data?.message || err.message || "Something went wrong."
      // );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("addFeedback")}>
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("feedbackType")}
              <span className="text-red-600">*</span>
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="feedback_status"
              value={formState.feedback_status}
              onChange={handleChange}
            >
              {Object.values(FEEDBACK_TYPE)?.map((list) => (
                <option key={list} value={list}>
                  {list}
                </option>
              ))}
            </select>
          </div>

          {/* Jurisdiction Transfer */}
          {formState.feedback_status === FEEDBACK_TYPE.APPROVED &&
            (investigationData?.prosecutor_decision_type ===
              "out of jurisdiction forward to zone" ||
              investigationData?.prosecutor_decision_type ===
                "Forwarded to zone for further investigation") &&
            adminUnitData?.hierarchy_level?.name ===
              HIERARCHY_LEVELS?.REGIONAL && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("forwardToZone")} <span className="text-red-600">*</span>
                </label>
                <select
                  required
                  className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                  value={selectedAdminUnit || ""}
                  onChange={(e) => setSelectedAdminUnit(e.target.value)}
                >
                  <option value="">{t("selectZoneAdministration")}</option>
                  {childAdminUnit?.map((list: any) => (
                    <option key={list.uu_id} value={list.uu_id}>
                      {list?.unit_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {t("caseWillBeTransferredToSelectedZonalAdministration")}
                </p>
              </div>
            )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("feedback")}
            </label>
            <textarea
              required
              rows={7}
              name="feedback"
              value={formState.feedback}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("addFeedback")
          )}
        </button>
      </form>
    </SideModal>
  );
}
